import { useEffect, useMemo, useRef, useState } from "react";
import { CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { getTaskDescriptionPreview } from "./taskDescriptionPreview";

/** Shows one line of instructions with a disclosure only when text is truncated. */
export function ThreadTaskDescriptionPreview({
  description,
  subject,
  expanded,
}: {
  description: string;
  subject: string;
  expanded: boolean;
}) {
  const preview = useMemo(
    () => getTaskDescriptionPreview(description),
    [description],
  );
  const rowRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const row = rowRef.current;
    const text = textRef.current;
    if (!row || !text) {
      return;
    }
    const measure = () => setTruncated(text.scrollWidth > row.clientWidth);
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    measure();
    return () => observer.disconnect();
  }, [preview]);

  let label = "See more";
  if (expanded) {
    label = "See less";
  }

  return (
    <div ref={rowRef} className="flex h-6 min-w-0 items-center gap-2 text-xs">
      <span
        ref={textRef}
        className={cn(
          "min-w-0 flex-1 truncate text-muted-foreground",
          expanded && "invisible",
        )}
        aria-hidden={expanded}
      >
        {preview}
      </span>
      {(truncated || expanded) && (
        <CollapsibleTrigger
          className="relative z-10 shrink-0 rounded-sm py-1 font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`${label} for ${subject}`}
        >
          {label}
        </CollapsibleTrigger>
      )}
    </div>
  );
}
